import { Button } from '@astryxdesign/core/Button';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { VStack } from '@astryxdesign/core/VStack';
import { VisuallyHidden } from '@astryxdesign/core/VisuallyHidden';
import { WarningCircle, WifiSlash } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { errorCode, errorText } from '@/lib/errors';

export function LoadingRows({ rows = 3, label = 'Загрузка' }: { rows?: number; label?: string }) {
  return (
    <VStack gap={2} aria-busy="true">
      <VisuallyHidden as="p">{label}…</VisuallyHidden>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={56} index={i} />
      ))}
    </VStack>
  );
}

export function ErrorView({ error, onRetry, compact }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  const offline = errorCode(error) === 'network';
  return (
    <EmptyState
      isCompact={compact}
      icon={offline ? <WifiSlash size={32} /> : <WarningCircle size={32} />}
      title={offline ? 'Нет связи' : 'Не удалось загрузить'}
      description={errorText(error)}
      actions={onRetry ? <Button label="Повторить" onClick={onRetry} /> : undefined}
    />
  );
}

export function EmptyView({ title, description, action, icon }: { title: string; description?: string; action?: ReactNode; icon?: ReactNode }) {
  return <EmptyState isCompact title={title} description={description} actions={action} icon={icon} />;
}

interface QueryLike<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

/** loading / error / empty / success for any TanStack query. */
export function QueryView<T>({
  query,
  isEmpty,
  empty,
  loading,
  children,
}: {
  query: QueryLike<T>;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  loading?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <>{loading ?? <LoadingRows />}</>;
  if (query.isError || query.data === undefined) return <ErrorView error={query.error} onRetry={() => void query.refetch()} />;
  if (isEmpty?.(query.data)) return <>{empty}</>;
  return <>{children(query.data)}</>;
}
